import axios from "axios";
import { logError, logInfo } from "../Utils/loggers";
import { getConsumerKey } from "./maerskApiTokenService";
import { useMsal } from "@azure/msal-react";
import { fetchToken } from "./fetchTokenService";
import { loginRequest } from "../auth/authConfig";

// Helper function for retried axios requests
const axiosWithRetry = async (config, retries = 3, delay = 1000) => {
  try {
    return await axios.request(config);
  } catch (error) {
    const isRetryable = !error.response || (error.response?.status >= 500 && error.response?.status < 600) || !error.response;
    if (retries > 0 && isRetryable) {
      await new Promise((res) => setTimeout(res, delay));
      return axiosWithRetry(config, retries - 1, delay * 2);
    }
    throw error;
  }
};

export const JiraProjectServices = () => {
  const jiraApiBaseUrl = "/jira-api";

  const jiraServerURl = process.env.REACT_APP_JIRA_URL;
  const { instance, accounts } = useMsal();

  const createAuthHeaders = async () => {
    const tokenResponse = await fetchToken(instance, loginRequest, accounts);
    return {
      Authorization: `Bearer ${tokenResponse.idToken}`,
      "consumer-key": getConsumerKey(),
      Accept: "application/json",
    };
  };

  // app.get("/jira-api/project/recent", async (req, res) => {
  //   const options = {
  //     method: "GET",
  //     url: "https://maersk-tools.atlassian.net/rest/api/3/project/recent",
  //     headers: {
  //       Authorization: `${jira_token}`,
  //       Accept: "application/json",
  //     },
  //   };
  //   try {
  //     const response = await axios.request(options);
  //     res.status(200).json(response.data);;
  //   } catch (error) {
  //     console.error(error);
  //     res.status(500).json({ error: error.message });
  //   }
  // });

  const getJiraProjectService = async () => {
    try {
      const headers = await createAuthHeaders();
      const config = {
        method: "GET",
        url: `${jiraServerURl}/jira-api/project/recent`,
        headers,
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error fetching Jira project:", error);
      throw error;
    }
  };

  const getProjectToSearchJiraService = async (query) => {
    let queryString = query.toUpperCase();
    try {
      const headers = await createAuthHeaders();
      const config = {
        method: "GET",
        url: `${jiraServerURl}/jira-api/project?query=${queryString}`,
        headers,
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error fetching Jira project:", error);
      throw error;
    }
  };

  const getIssueTypesBasedOnProject = async (projectId) => {
    try {
      const headers = await createAuthHeaders();
      const config = {
        method: "GET",
        url: `${jiraServerURl}/jira-api/project/${projectId}`,
        headers,
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error fetching Jira project:", error);
      throw error;
    }
  };

  const getParentsBasedOnIssues = async (issueName, projectName) => {
    try {
      const headers = await createAuthHeaders();
      const config = {
        method: "GET",
        url: `${jiraServerURl}/jira-api/parent/${issueName}/${projectName}`,
        headers,
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error fetching Jira project:", error);
      throw error;
    }
  };

  const jiraAccountId = async (email) => {
    try {
      const headers = await createAuthHeaders();
      const config = {
        method: "GET",
        url: `${jiraServerURl}/jira-api/account-id/${email}`,
        headers,
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error fetching Jira project:", error);
      throw error;
    }
  };

  const jiraTicketCreate = async (data) => {
    const projectId = data?.project?.id;
  
    const issueLabel =
      data?.issue?.label ??
      data?.issueType?.label ??
      data?.issue?.name ??
      data?.issueType?.name ??
      "";
  
    const isTaskIssue =
      typeof issueLabel === "string" &&
      issueLabel.trim().toLowerCase() === "task";
  
    let payload = { ...data };
  
    try {
      if (isTaskIssue) {
        // Resolve Epic key from epicId (parent.id/value/key) for Task issues
        const epicId =
          data?.parent?.id ?? data?.parent?.value ?? data?.parent?.key ?? null;
  
        if (epicId) {
          const headers = await createAuthHeaders();
          const config = {
            method: "GET",
            url: `${jiraServerURl}/jira-api/epic/${encodeURIComponent(epicId)}/key`,
            headers,
          };
          const epicResp = await axiosWithRetry(config, 3, 1000);
          const epicKey = epicResp.data?.key; // e.g., "SB-1028"
  
          if (projectId && epicKey) {
            payload = {
              ...payload,
              customfield_10002: epicKey, // Epic Link
            };
          }
        }
  
        // Remove parent for Task issues
        if (payload.parent) delete payload.parent;
      } else {
        // Non-Task: use parent label/name/key into customfield_<projectId>
        const parentLabelVal =
          data?.parent?.label ?? data?.parent?.name ?? data?.parent?.key;
  
        if (projectId && parentLabelVal) {
          payload = {
            ...payload,
            [`customfield_${projectId}`]: parentLabelVal,
          };
        }
      }
    } catch (err) {
      console.error("Failed resolving Epic key for Task:", err);
      if (isTaskIssue && payload.parent) delete payload.parent;
    }
  
    try {
      const headers = await createAuthHeaders();
      const config = {
        method: "POST",
        url: `${jiraServerURl}/jira-api/issue`,
        headers,
        data: payload,
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error creating Jira issue:", error.response?.data || error.message);
      throw error;
    }
  };

  return {
    getJiraProjectService,
    getProjectToSearchJiraService,
    getIssueTypesBasedOnProject,
    getParentsBasedOnIssues,
    jiraAccountId,
    jiraTicketCreate,
  };
};
